import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useExplainMode } from './ExplainContext';
import explanations from './explanations';
import './css/explain.css';

// <Explain concept="activation">…</Explain>  — concept text from explanations.js
// <Explain title="…" text="…">…</Explain>    — inline text (e.g. lens descriptions from the backend)
// graphKey — override the key used to find the concept's in-graph definition
//            object (defaults to `concept`); when it resolves, the popover adds
//            an "explore in the graph" link to that object (wave 4).
//
// With explain mode off this renders its children untouched. With it on, a small
// ⓘ marker appears next to the children; clicking it opens a popover explaining
// the concept using whatever live data the children are showing. Markers stop
// event propagation so they work inside links and clickable rows.
export function Explain({ concept, title, text, graphKey, children }) {
  const { on, concepts } = useExplainMode();
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const close = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [open]);

  // Position the popover as a fixed overlay from the marker's measured rect
  // (the claim-popover pattern): it floats above the page everywhere, immune
  // to ancestor overflow clipping — scrollable cards, the Reader's fixed
  // claim popover and bottom sheet, 280px margin cards. Scrolling closes it
  // rather than letting it drift away from its marker.
  useEffect(() => {
    if (!open || !wrapRef.current) return;
    const r = wrapRef.current.getBoundingClientRect();
    const width = Math.min(260, window.innerWidth - 24);
    const left = Math.max(12, Math.min(r.left, window.innerWidth - width - 12));
    const top = Math.min(r.bottom + 6, window.innerHeight - 120);
    setPos({ top, left, width, maxHeight: window.innerHeight - top - 12 });
    const close = (e) => {
      // scrolling the popover's own overflow shouldn't dismiss it
      if (e && wrapRef.current && e.target instanceof Node && wrapRef.current.contains(e.target)) return;
      setOpen(false);
    };
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [open]);

  if (!on) return children ?? null;

  const entry = concept ? explanations[concept] : null;
  const popTitle = title ?? entry?.title ?? '';
  const popText = text ?? entry?.text ?? `Missing explanation: ${concept}`;
  const graphConcept = concepts[graphKey ?? concept];

  const stop = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <span className="explain-wrap" ref={wrapRef}>
      {children}
      <button
        type="button"
        className="explain-marker"
        aria-label={`Explain: ${popTitle}`}
        aria-expanded={open}
        onClick={(e) => {
          stop(e);
          setOpen((v) => !v);
        }}
      >
        i
      </button>
      {open && pos && (
        <span
          className="explain-pop"
          role="note"
          style={{ position: 'fixed', top: pos.top, left: pos.left, width: pos.width, maxHeight: pos.maxHeight, overflowY: 'auto', zIndex: 120 }}
          onClick={(e) => {
            // Popovers can sit inside clickable cards/links; swallow the click
            // unless it landed on the popover's own explore-in-graph link.
            e.stopPropagation();
            const a = e.target.closest && e.target.closest('a');
            if (!a || !e.currentTarget.contains(a)) e.preventDefault();
          }}
        >
          {popTitle && <strong className="explain-pop-title">{popTitle}</strong>}
          {popText}
          {graphConcept && (
            <span style={{ display: 'block', marginTop: '6px' }}>
              <Link to={`/object/${graphConcept.object_id}`} style={{ fontSize: '12px' }}>
                explore “{graphConcept.term}” in the graph →
              </Link>
            </span>
          )}
        </span>
      )}
    </span>
  );
}

// The nav control. Lives here so its CSS ships with the component.
export function ExplainToggle() {
  const { on, toggle } = useExplainMode();
  return (
    <button
      type="button"
      className={`explain-toggle${on ? ' on' : ''}`}
      onClick={toggle}
      title="Explain mode annotates scores and badges with how they are computed"
    >
      {on ? 'Explain: on' : 'Explain: off'}
    </button>
  );
}
